/**
 * Sistema de deduplicação de erros do Sentry
 * Previne spam de erros iguais, limitando a 10 erros por minuto por fingerprint
 */

// Map para armazenar contadores de erros por fingerprint
const errorCounts = new Map()

// Limite de erros por minuto
const MAX_ERRORS_PER_MINUTE = 10

// Intervalo em milissegundos (1 minuto)
const CLEANUP_INTERVAL = 60000

/**
 * Limpa os contadores de erro a cada minuto
 */
function cleanupCounters() {
  errorCounts.clear()
}

// Inicia o intervalo de limpeza
let cleanupInterval = setInterval(cleanupCounters, CLEANUP_INTERVAL)

/**
 * Verifica se um erro deve ser ignorado baseado na contagem
 * @param {string} fingerprint - Fingerprint do erro
 * @returns {boolean} - true se deve ignorar o erro, false caso contrário
 */
function shouldIgnoreError(fingerprint) {
  if (!fingerprint) {
    return false
  }

  const currentCount = errorCounts.get(fingerprint) || 0

  if (currentCount >= MAX_ERRORS_PER_MINUTE) {
    return true
  }

  // Incrementa o contador
  errorCounts.set(fingerprint, currentCount + 1)
  return false
}

/**
 * Obtém o fingerprint do evento do Sentry
 * @param {Object} event - Evento do Sentry
 * @returns {string} - Fingerprint do evento
 */
function getEventFingerprint(event) {
  // Tenta usar o fingerprint do evento se disponível
  if (event.fingerprint && event.fingerprint.length > 0) {
    return event.fingerprint.join('-')
  }

  // Fallback: cria fingerprint baseado em mensagem e stack trace
  const message = event.message || event.exception?.values?.[0]?.value || ''
  const stackTrace = event.exception?.values?.[0]?.stacktrace?.frames
    ?.map((frame) => `${frame.filename}:${frame.lineno}`)
    .join('-') || ''

  return `${message}-${stackTrace}`.substring(0, 200) // Limita tamanho
}

/**
 * Função beforeSend para o Sentry que implementa deduplicação
 * @param {Object} event - Evento do Sentry
 * @param {Object} hint - Hint do Sentry
 * @returns {Object|null} - Evento modificado ou null para ignorar
 */
export function deduplicationBeforeSend(event, hint) {
  const originalError = hint?.originalException
  const requestUrl = originalError?.config?.url || ''

  if (
    originalError?.isAxiosError === true &&
    originalError.message === 'Network Error' &&
    requestUrl.includes('/notifications/unread-count') &&
    !originalError.response
  ) {
    return null
  }

  const fingerprint = getEventFingerprint(event)

  if (shouldIgnoreError(fingerprint)) {
    // Log para debug (apenas em desenvolvimento)
    if (import.meta.env.DEV) {
      console.warn(
        `[Sentry] Erro ignorado por deduplicação: ${fingerprint.substring(0, 50)}...`
      )
    }
    return null // Ignora o evento
  }

  return event // Envia o evento
}

/**
 * Limpa o intervalo de limpeza (útil para testes)
 */
export function cleanup() {
  if (cleanupInterval) {
    clearInterval(cleanupInterval)
    cleanupInterval = null
  }
}

/**
 * Reinicia o sistema de deduplicação (útil para testes)
 */
export function reset() {
  cleanup()
  errorCounts.clear()
  cleanupInterval = setInterval(cleanupCounters, CLEANUP_INTERVAL)
}

// Exporta para debug
export { errorCounts }
